import { convertCurrency, convertToUSD, formatCurrency, formatDate } from 'src/utils/functions';
import { EditMode, FinancialKPIConfig, MetricsState, UserReportAccessTypes } from './propTypes';
import { inputSections } from './configs/FinancialInputConfig';
import { exchangeRates, monthNames } from 'src/utils/const';
import { OTA } from 'src/interfaces/idashboard';
import { User } from 'src/interfaces/iuser';

export const convertData = (input, localCurrency: null | string = null, ota: OTA = 'airbnb') => {
  const convertCurrencyWrapper = (value: number) => {
    const convertedCurrency =
      localCurrency && localCurrency !== 'USD' && !isNaN(value)
        ? convertToUSD(localCurrency, value)
        : value;
    return convertedCurrency;
  };

  if (ota === 'ltr') {
    return {
      listingID: input.id,
      listingURL: input.url,
      bedrooms: input.bedrooms,
      latitude: input.latitude,
      longitude: input.longitude,
      rent_price: input.rent_price,
      rent_price_period: 'month',
      room_type: input.property_type || 'Rental Unit',
      bathrooms: input.bathrooms,
      amenities: input.amenities,
      listed_on: input.listed_on,
      thumbnail_url: input.thumbnail_url,
      property_size: input.property_size,
      property_size_unit: input.property_size_unit,
      street_address: input.street_address,
      is_selected: 1,
    };
  }

  // Convert booked_daily_rate_ltm_monthly to adr
  const adr = input.booked_daily_rate_ltm_monthly
    ? Object.entries(input.booked_daily_rate_ltm_monthly).map(([date, value]) => ({
        date,
        adr:
          value !== null && value !== undefined
            ? convertCurrencyWrapper(value as number)
            : 'missing',
      }))
    : [];

  // Convert occupancy_rate_ltm_monthly to occupancy
  const occupancy = input.occupancy_rate_ltm_monthly
    ? Object.entries(input.occupancy_rate_ltm_monthly).map(([date, value]) => ({
        date,
        occupancy: value !== null && value !== undefined ? value : 'missing',
      }))
    : [];

  // Convert revenue_ltm_monthly to revenue
  const revenue = input.revenue_ltm_monthly
    ? Object.entries(input.revenue_ltm_monthly).map(([date, value]) => ({
        date,
        revenue:
          value !== null && value !== undefined
            ? convertCurrencyWrapper(value as number)
            : 'missing',
      }))
    : [];

  // Convert no_of_bookings_ltm_monthly to bookings
  const bookings = input.no_of_bookings_ltm_monthly
    ? Object.entries(input.no_of_bookings_ltm_monthly).map(([date, value]) => ({
        date,
        booking: value !== null && value !== undefined ? value : 'missing',
      }))
    : [];

  // Prepare the final output object
  const output = {
    // markerType: 'full',
    listingID: input.listingID,
    name: input.name,
    thumbnail_url: input.thumbnail_url,
    latitude: input.latitude,
    longitude: input.longitude,
    room_type: input.room_type,
    property_type: '-1',
    bedrooms: input.bedrooms,
    bathrooms: input.bathrooms,
    accommodates: input.accommodates,
    host_name: input.host_name,
    minimum_nights: input.minimum_nights,
    cleaningFee: convertCurrencyWrapper(input.cleaning_fee),
    extraGuestFee: 0, // Assuming 0 as extra guest fee is not provided
    hostId: input.host_id,
    annualRevenueLTM: convertCurrencyWrapper(input.annual_revenue_ltm),
    host_listing_count: null, // Assuming null as host listing count is not provided
    review_scores_rating: input.reveiw_scores_rating,
    amenities: input.amenities,
    visible_review_count: input.visible_review_count,
    adr: adr,
    occupancy: occupancy,
    revenue: revenue,
    bookings: bookings,
    activeDaysCount: input.active_days_count_ltm,
    last_seen: null, // TODO: Add last seen date
    confidenceScoreRelatedData: {
      dataQualityScore: 91, // Assuming fixed data quality score as it's not in input
      distance: 1606, // Assuming a fixed distance as it's not in input
      isNumberOfBathroomDifference: false, // Assuming false as it's not in input
      isHouseTypeDifference: false, // Assuming false as it's not in input
      confidenceScore: 95.5, // Assuming a fixed confidence score as it's not in input
    },
    revenuePotential: convertCurrencyWrapper(input.revenue_potential),
    occupancyRateLTM: input.avg_occupancy_rate_ltm,
    adrLTM: convertCurrencyWrapper(input.avg_booked_daily_rate_ltm),
    no_of_bookings_ltm: input.no_of_bookings_ltm,
    is_premium: true, // Assuming true as is_premium is not provided
    rest_thumbnails: input.thumbnail_url_extended,
    similarity_score: input.similarity_score,
    similarity_score_meta: input.similarity_score_meta,
    rank: input.rank,
    listingURL: input.listing_url,
    // Backend locks listing metrics to 1234 when user lacks access; detect so UI can show lock
    isMockListingData:
      input.annual_revenue_ltm === 1234 && input.revenue_potential === 1234,
  };

  return output;
};

export const checkIfUserAllowedToEditReport = (userEmail: string, reportEmail: string | null) => {
  if (userEmail === 'Trial') {
    return false;
  }

  if (!reportEmail) {
    return false;
  }

  if (userEmail === reportEmail) {
    return true;
  }

  return false;
};

export const convertApiCurrency = (
  apiOutput: any,
  currency: string,
  convertBackToUSD: boolean = false,
  isSendingBackToApi: boolean = false
) => {
  const key_value_type_map = {
    property_price: 'int',
    monthly_rent: 'int',
    ltm_revenue: 'int',
    ltm_nightly_rate: 'int',
    monthly_revenue: 'int',
    monthly_adr: 'int',
    yearly_net_profit_before_tax: 'int',
    monthly_profit: 'int',
    monthly_expense: 'int',
    one_time_cost: 'int',
    is_loan_taken: 'int',
    loan_tenure: 'int',
    down_payment: 'int',
    hoa_fee_annual: 'int',
    maintenance_annual: 'int',
    insurance_annual: 'int',
    electricity_annual: 'int',
    wifi_annual: 'int',
    gas_annual: 'int',
    others_annual: 'int',
    furnishing_cost: 'int',
    closing_cost: 'int',
    property_appreciation_rate: 'int',
    owner_occupied_days: 'int',
    // Adding new currency-related fields
    property_tax: 'int',
    mortgage_payment: 'int',
    property_management_fee: 'int',
    service_fees: 'int',
    // Additional fields from report
    property_value: 'int',
    cleaning_fee: 'int',
    extra_guest_fee: 'int',
    annual_revenue_ltm: 'int',
    revenue_potential: 'int',
    avg_booked_daily_rate_ltm: 'int',
    total_expenses: 'int',
    net_operating_income: 'int',
    five_years_cash_flow: 'int',
    total_cash_invested: 'int',
    total_profit_when_sold: 'int',
    startup_costs_total: 'int',
    remodelling: 'int',
    mortgage_annual: 'int',
    income_tax: 'int',
    depreciation: 'int',
    taxes_total: 'int',
    cost_to_sell: 'int',
    vacancy_cost: 'int',
    lodging_tax: 'int',
    tech: 'int',
    marketing: 'int',
    total_revenue: 'int',
    total_net_operating_income: 'int',
  };

  const currencyKeys = [
    'hoa_fee_annual',
    'maintenance_annual',
    'insurance_annual',
    'electricity_annual',
    'wifi_annual',
    'gas_annual',
    'others_annual',
    'annual_operating_expenses_total',
    'furnishing_cost',
    'closing_cost',
    'startup_costs_total',
    'property_price',
    'monthly_rent',
    'ltm_revenue',
    'ltm_nightly_rate',
    'monthly_revenue',
    'monthly_adr',
    'yearly_net_profit_before_tax',
    'monthly_profit',
    'yearly_expense',
    'cleaning_fee',
    'annual_revenue_ltm',
    'revenue_potential',
    'avg_booked_daily_rate_ltm',
    'booked_daily_rate_ltm_monthly',
    'revenue_ltm_monthly',
    'monthly_expense',
    'one_time_cost',
    'down_payment',
    'property_tax',
    'mortgage_payment',
    'property_management_fee',
    'service_fees',
    'property_value',
    'total_expenses',
    'ltm_revenue',
    'ltm_nightly_rate',
    'net_operating_income',
    'yearly_net_profit_before_tax',
    'five_years_cash_flow',
    'total_cash_invested',
    'total_profit_when_sold',
    'remodelling',
    'mortgage_annual',
    'income_tax',
    'depreciation',
    'taxes_total',
    'cost_to_sell',
    'vacancy_cost',
    'lodging_tax',
    'tech',
    'marketing',
    'total_revenue',
    'total_net_operating_income',
    'hotel_nightly_rate',
  ];

  const convertCurrencyWrapper = (currency: string, value: number, key: string) => {
    let convertedCurrency: number | null = null;
    if (convertBackToUSD) {
      convertedCurrency = convertToUSD(currency, value);
    } else {
      convertedCurrency = convertCurrency(currency, value);
    }

    if (isSendingBackToApi && key_value_type_map[key] === 'int') {
      convertedCurrency = Math.round(convertedCurrency);
    }

    return convertedCurrency;
  };

  const convertValues = (obj: any) => {
    for (let key in obj) {
      if (currencyKeys.includes(key)) {
        if (typeof obj[key] === 'number') {
          // Convert simple numeric values
          obj[key] = convertCurrencyWrapper(currency, obj[key], key);
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          // Check if the object contains date keys with numeric values
          // Handles monthly data : e.g: annual_revenue_monthly: {date: value}[]
          for (let dateKey in obj[key]) {
            if (typeof obj[key][dateKey] === 'number') {
              obj[key][dateKey] = convertCurrencyWrapper(currency, obj[key][dateKey], key);
            }
          }
        }
      } else if (key.includes('range')) {
        obj[key] = [
          convertCurrencyWrapper(currency, obj[key][0], key),
          convertCurrencyWrapper(currency, obj[key][1], key),
        ];
      } else if (typeof obj[key] === 'object' && obj[key] !== null) {
        // Recursively handle nested objects
        convertValues(obj[key]);
      }
    }
  };

  convertValues(apiOutput);

  return apiOutput;
};

export const getIfReportTypeDisabled = (
  reportType: UserReportAccessTypes,
  uiElement: string,
  elementIndex: number = 0,
  user: User
): boolean => {
  // Scale and expert always get real individual listing data in comps (never disabled for listing/marker)
  if (user?.subscriptionPlan && ['scale', 'expert'].includes(user.subscriptionPlan)) {
    if (
      ['CompsFilterListing', 'ListingMarker', 'ListingMarkerCardLTMHistoryVisibility'].includes(
        uiElement
      )
    ) {
      return false;
    }
  }
  switch (uiElement) {
    case 'RevenueLTMGraph':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'CompsFilterListing':
      if (user.subscriptionPlan === 'starter' && elementIndex >= 0) return true;
      break;
    case 'CompsLtmRevenueBars':
      if (['starter', 'pro'].includes(reportType)) return true;
      break;
    case 'CompsFilterFetchMoreListingsButton':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'CompsFilterListingVisibilityToggleSwitch':
      if (['starter'].includes(reportType)) return true;
      break;
    case 'ListingMarker':
      if (user.subscriptionPlan === 'starter' && elementIndex >= 0) return true;
      break;
    case 'ListingMarkerCardLTMHistoryVisibility':
      if (['starter', 'pro'].includes(reportType)) return true;
      break;
  }

  return false;
};

export const feetToMeterConversion = (value: number) => {
  const conversionFactor = 3.28084;
  const convertedValue = value / conversionFactor;
  return convertedValue;
};

export const meterToFeetConversion = (value: number) => {
  const conversionFactor = 3.28084;
  const convertedValue = value * conversionFactor;
  return convertedValue;
};

export const initializeMetricsState = (
  config: Record<string, FinancialKPIConfig>,
  reportType: EditMode
) => {
  const state: MetricsState = {};
  const presetVisibilityString = localStorage.getItem('kpi_visibility') || '';
  const presetVisibility = presetVisibilityString ? JSON.parse(presetVisibilityString) : {};

  for (const key in config) {
    const metricConfig = config[key];
    const isAllowed = metricConfig.allowedModes.includes(reportType);
    const isDefaultVisible = metricConfig.showAsDeafaultIn.includes(reportType);

    state[key] = {
      visibility: isAllowed
        ? presetVisibility && key in presetVisibility
          ? presetVisibility[key]
          : isDefaultVisible
        : isDefaultVisible,
      config: metricConfig,
      allowed: isAllowed,
    };
  }

  return state;
};

export const initializeInputEditState = kpiData => {
  const initialState: Record<string, any> = {};

  Object.values(inputSections).forEach(sectionConfig => {
    if (sectionConfig.canToggle && sectionConfig.toggleKey) {
      initialState[sectionConfig.toggleKey] =
        kpiData?.[sectionConfig.toggleKey] == 1 ? true : false;
    }

    Object.keys(sectionConfig.fields).forEach(fieldKey => {
      initialState[fieldKey] =
        kpiData?.[fieldKey] ||
        (sectionConfig.fields[fieldKey].isDropdown
          ? sectionConfig?.fields?.[fieldKey]?.dropdownOptions?.[0] || 0
          : 0);
    });
  });

  return initialState;
};

export const formatDateYearmonthName = (date: string) => {
  const year = date.split('-')[0];
  const month = date.split('-')[1];
  const monthName = Number(month) ? monthNames[Number(month) - 1] : '';
  return `${year} ${monthName}`;
};

export const getLtmDates = (kpiData: any) => {
  try {
    let months =
      kpiData && kpiData[50] && kpiData[50].monthly_adr ? Object.keys(kpiData[50].monthly_adr) : [];
    if (months.length === 0) {
      return '';
    }
    // months: 2024-01, 2024-02, .... month string
    // Sort the months by dates
    months.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA.getTime() - dateB.getTime();
    });
    const lastMonth = months[months.length - 1];
    const monthStartingOfTheYear = months[months.length - 12];

    const formattedLastMonth = formatDateYearmonthName(lastMonth);
    const formattedMonthStartingOfTheYear = formatDateYearmonthName(monthStartingOfTheYear);

    return `${formattedMonthStartingOfTheYear} - ${formattedLastMonth}`;
  } catch (error) {
    return '';
  }
};

export const processSubmarketsCurrency = (currency: string, submarketsData: any) => {
  const currencySymbol = exchangeRates[currency].symbol;

  if (!submarketsData || !submarketsData.length) return [];

  return [...submarketsData].map((submarket: any) => {
    return {
      ...submarket,
      adr: currencySymbol + formatCurrency(String(convertCurrency(currency, submarket.adr))),
      revenue:
        currencySymbol + formatCurrency(String(convertCurrency(currency, submarket.revenue))),
      occupancy_rate: submarket.occupancy_rate + '%',
      supply: formatCurrency(String(submarket.supply)),
    };
  });
};

const getVisiblePText = (p: any) => {
  return Array.from(p.childNodes)
    .filter((n: any) => n.nodeType === Node.TEXT_NODE)
    .map((n: any) => n.textContent)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};

export const parseForecastHTML = (root: any) => {
  /*
    This function takes the Forecast Your Income section as input
    Sends the visible title and value to the backend to process.
  */

  if (!root) return [null, null];

  const result: any[] = [];

  // grab all <h2> sections - these are the main section boundaries
  const h2s = Array.from(root.querySelectorAll('h2'));

  // tree walker to move smoothly through elements in order
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);

  const collectBetween = (startNode: any, endNode: any) => {
    // position walker at startNode
    walker.currentNode = root;
    while (walker.currentNode && walker.currentNode !== startNode) {
      if (!walker.nextNode()) break;
    }
    const pList: any[] = [];
    const h5List: any[] = [];

    // advance once so we start *after* the h2
    walker.nextNode();

    // collect <p> and <h5> until the next <h2> (or end)
    while (walker.currentNode && walker.currentNode !== endNode) {
      const el = walker.currentNode as any;
      if (el.tagName === 'P') pList.push(el as any);
      else if (el.tagName === 'H5') h5List.push(el as any);
      if (!walker.nextNode()) break;
    }

    const data: any[] = [];
    const len = Math.max(pList.length, h5List.length);
    for (let i = 0; i < len; i++) {
      const title = getVisiblePText(pList[i]);
      if (!title) continue;
      const raw = h5List[i]?.textContent?.trim() as any | null;
      data.push({ title, value: raw || '-' });
    }
    return data;
  };

  for (let i = 0; i < h2s.length; i++) {
    const h2 = h2s[i] as any;
    const nextH2 = h2s[i + 1] || null;
    const section = h2.textContent.trim() as any;
    const data = collectBetween(h2, nextH2);
    result.push({ section, data } as any);
  }

  const futureProjectionGraph = root.querySelector('svg.recharts-surface');

  return [
    result.slice(0, result.length - 1),
    futureProjectionGraph ? futureProjectionGraph.outerHTML : null,
  ];
};

export const processNearestMarketBasicKPIDetails = (
  nearestMarketBasicKPIDetails: any,
  currency: string
) => {
  const currencySymbol = exchangeRates[currency].symbol;

  if (!nearestMarketBasicKPIDetails) return null;

  nearestMarketBasicKPIDetails = { ...nearestMarketBasicKPIDetails };

  nearestMarketBasicKPIDetails.adr =
    currencySymbol +
    formatCurrency(String(convertCurrency(currency, nearestMarketBasicKPIDetails.adr)));
  nearestMarketBasicKPIDetails.occupancyRate = nearestMarketBasicKPIDetails.occupancyRate + '%';
  nearestMarketBasicKPIDetails.supply = formatCurrency(String(nearestMarketBasicKPIDetails.supply));

  return nearestMarketBasicKPIDetails;
};

export const processLtrData = (ltrData: any, currency: string) => {
  const currencySymbol = exchangeRates[currency].symbol;

  if (!ltrData || !ltrData.data) return null;

  for (const comp of ltrData.data.comps) {
    comp.rent_price =
      currencySymbol + formatCurrency(String(convertCurrency(currency, comp.rent_price)));
  }

  return ltrData;
};

export const getIsEditedKpiAvailable = reportData => {
  if (!reportData) return { isEditedKpiAvailable: false, editedKpiKeys: [] };

  const data50 = reportData.kpi?.[50];
  const dataEdited = reportData.kpi?.['edited'];

  if (!data50 || !dataEdited) return { isEditedKpiAvailable: false, editedKpiKeys: [] };
  if (Object.keys(data50).length === 0 || Object.keys(dataEdited).length === 0)
    return { isEditedKpiAvailable: false, editedKpiKeys: [] };

  const editedKpiKeys: ('ltm_revenue' | 'ltm_occupancy_rate' | 'ltm_nightly_rate')[] = [];
  if (data50['ltm_revenue'] !== dataEdited['ltm_revenue']) {
    editedKpiKeys.push('ltm_revenue');
  }
  if (data50['ltm_occupancy_rate'] !== dataEdited['ltm_occupancy_rate']) {
    editedKpiKeys.push('ltm_occupancy_rate');
  }
  if (data50['ltm_nightly_rate'] !== dataEdited['ltm_nightly_rate']) {
    editedKpiKeys.push('ltm_nightly_rate');
  }

  if (editedKpiKeys.length > 0) {
    return { isEditedKpiAvailable: true, editedKpiKeys };
  }

  return { isEditedKpiAvailable: false, editedKpiKeys: [] };
};
